import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { fetchMemberProductExperience } from "../services/memberProductExperience";
import { canAccessOfficeCentral } from "../utils/accessControl";

function ArrowIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SparkIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8L12 3z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  );
}

function NotebookIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
      <path d="M7 4.5H17C18.1 4.5 19 5.4 19 6.5V19.5H7C5.9 19.5 5 18.6 5 17.5V6.5C5 5.4 5.9 4.5 7 4.5Z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8.5 4.5V19.5M11 8H16M11 11H15" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
    </svg>
  );
}

function productActionRoute(product) {
  const action = product?.next_action;
  if (!action || action.type !== "lesson" || !action.system_id || !action.resource_id) {
    return null;
  }
  return `/core/day/${action.resource_id}`;
}

function accessLabel(mode) {
  return mode === "read_only" ? "Consulta" : "En curso";
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user, refreshSession } = useAuth();
  const hasOfficeAccess = canAccessOfficeCentral(user);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [productsError, setProductsError] = useState("");

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  useEffect(() => {
    let cancelled = false;

    async function loadProductExperience() {
      try {
        setLoadingProducts(true);
        setProductsError("");
        const items = await fetchMemberProductExperience();
        if (!cancelled) setProducts(items);
      } catch (error) {
        if (!cancelled) setProductsError("No pudimos cargar tus productos en este momento.");
      } finally {
        if (!cancelled) setLoadingProducts(false);
      }
    }

    loadProductExperience();
    return () => {
      cancelled = true;
    };
  }, []);

  // Account spaces are platform capabilities, not commercial products.
  // Their concrete access policy remains independent: Bitácora is account history;
  // Conexión keeps its current commercial guard until capabilities are entitlement-driven.
  const accountSpaces = useMemo(
    () => [
      {
        id: "bitacora",
        title: "Bitácora ZEDRO",
        subtitle: "Conserva en un solo lugar tus reflexiones, decisiones, evidencias y aprendizajes para consultar tu recorrido cuando lo necesites.",
        note: "Historial de tu cuenta",
        icon: <NotebookIcon />,
        route: "/bitacora",
      },
      {
        id: "conexion",
        title: "Conexión ZEDRO",
        subtitle: "Una experiencia guiada para acudir a la Palabra, enfocar tu oración y expresar con claridad lo que estás viviendo.",
        note: "Experiencia guiada",
        icon: <SparkIcon />,
        route: "/conexion",
      },
    ],
    []
  );

  return (
    <main style={{ minHeight: "100vh", background: "var(--page-bg)" }}>
      <div className="z-dashboard-shell">
        <section className="z-dashboard-hero">
          <div className="z-dashboard-kicker">ZEDRO Academy</div>
          <h1 className="z-dashboard-title">{user?.name ? `Hola, ${user.name}` : "Hola"}</h1>
          <p className="z-dashboard-subtitle">Aquí encuentras tus productos, tus espacios personales y la próxima acción disponible para ti.</p>
        </section>

        {hasOfficeAccess && (
          <section className="z-dashboard-section" style={{ marginBottom: "2rem" }}>
            <h2 className="z-dashboard-sectionTitle">Oficina Central</h2>
            <div className="z-premium-card z-premium-card--module" style={{ cursor: "pointer" }} onClick={() => navigate("/admin") }>
              <div className="z-card-top">
                <span className="z-card-label">Administración</span>
                <div className="z-card-icon"><SparkIcon /></div>
              </div>
              <div>
                <h3 className="z-card-title z-card-title--sm">Gestión Empresarial</h3>
                <p className="z-card-copy">Producción, productos, miembros, suscripciones y operación interna.</p>
              </div>
              <div className="z-card-actions"><span className="z-card-link">Entrar <ArrowIcon /></span></div>
            </div>
          </section>
        )}

        <section className="z-dashboard-section">
          <h2 className="z-dashboard-sectionTitle">Tus productos</h2>

          {loadingProducts && <div className="z-premium-card z-premium-card--module"><p className="z-card-copy">Cargando tu experiencia...</p></div>}

          {!loadingProducts && productsError && (
            <div className="z-premium-card z-premium-card--module">
              <p className="z-card-copy">{productsError}</p>
            </div>
          )}

          {!loadingProducts && !productsError && products.length === 0 && (
            <div className="z-premium-card z-premium-card--module">
              <h3 className="z-card-title z-card-title--sm">Aún no tienes productos activos</h3>
              <p className="z-card-copy">Cuando adquieras o recibas acceso a un producto, aparecerá aquí con su estado y recorrido correspondiente.</p>
              <div className="z-card-actions"><button type="button" className="z-card-link z-dashboard-linkBtn" onClick={() => navigate("/store")}>Explorar productos <ArrowIcon /></button></div>
            </div>
          )}

          {!loadingProducts && !productsError && products.length > 0 && (
            <div className="z-dashboard-grid z-dashboard-grid--ecosystem">
              {products.map((product) => {
                const route = productActionRoute(product);
                const isReadOnly = product.access_mode === "read_only";
                const progressPercent = Math.min(
                  100,
                  Math.max(0, Number(product.progress_percent || 0)),
                );

                return (
                  <article key={product.id} className="z-premium-card z-premium-card--module">
                    <div className="z-card-top">
                      <span className="z-card-label">{accessLabel(product.access_mode)}</span>
                      <span
                        className="z-card-meta z-card-meta--clean"
                        role="progressbar"
                        aria-label={`Progreso de ${product.name}`}
                        aria-valuemin="0"
                        aria-valuemax="100"
                        aria-valuenow={progressPercent}
                      >
                        {progressPercent}%
                      </span>
                    </div>
                    <div>
                      <h3 className="z-card-title z-card-title--sm">{product.name}</h3>
                      {product.description && <p className="z-card-copy">{product.description}</p>}
                    </div>
                    <div className="z-card-footer">
                      <span className="z-card-meta z-card-meta--clean">
                        {isReadOnly
                          ? "Tu acceso de consulta está disponible."
                          : product.next_action?.title
                            ? `Próximo: ${product.next_action.title}`
                            : "No hay una nueva acción pendiente."}
                      </span>
                      {route && !isReadOnly && (
                        <button type="button" className="z-card-link z-card-link--minimal z-dashboard-linkBtn" onClick={() => navigate(route)}>
                          Continuar <ArrowIcon />
                        </button>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>

        <section className="z-dashboard-section">
          <h2 className="z-dashboard-sectionTitle">Tu espacio ZEDRO</h2>
          <div className="z-dashboard-grid z-dashboard-grid--ecosystem">
            {accountSpaces.map((space) => (
              <button key={space.id} type="button" className="z-premium-card z-premium-card--module z-dashboard-moduleBtn" onClick={() => navigate(space.route)}>
                <div className="z-card-top">
                  <h3 className="z-card-title z-card-title--sm" style={{ margin: 0 }}>{space.title}</h3>
                  <div className="z-card-icon z-card-icon--soft">{space.icon}</div>
                </div>
                <p className="z-card-copy z-card-copy--compact">{space.subtitle}</p>
                <div className="z-card-footer">
                  <span className="z-card-meta z-card-meta--clean">{space.note}</span>
                  <span className="z-card-link z-card-link--minimal">Abrir <ArrowIcon /></span>
                </div>
              </button>
            ))}
          </div>
        </section>
      </div>
    </main>
  );
}
